import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Body1,
  Caption1,
  Input,
  Dropdown,
  Option,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Badge,
} from '@fluentui/react-components';
import { Search20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { FamilyBadge, SeverityBadge, StatusBadge } from '../components/StatusBadge';
import { FrameworkInlineText } from '../components/FrameworkBadge';
import { controls, familyLabels } from '../domain/controls';
import { useActiveTenant } from '../hooks/useActiveTenant';
import { useLatestScan } from '../hooks/useScans';
import { ControlDefinition, ControlReference, Family, Severity } from '../domain/types';

type StatusFilter = 'all' | 'pass' | 'fail' | 'warning' | 'manual';

// Couleurs niveaux CyFun (alignees sur CyFunMappingPage)
const CYFUN_LEVEL_COLOR: Record<NonNullable<ControlReference['cyfunLevel']>, { fg: string; bg: string }> = {
  Basic:     { fg: '#1F7A45', bg: '#E6F4EC' },
  Important: { fg: '#A86317', bg: '#FDF1E2' },
  Essential: { fg: '#B82329', bg: '#FBE7E7' },
};

// Note : highestCyfunLevel() retiree - on affiche maintenant TOUS les tags
// CyFun individuellement (cf. cyfunLevels = c.references...). Cf. demande user
// "je préfère que tu mettes tous les tag et pas x2".

export function ControlsPage() {
  const { t }           = useTranslation();
  const navigate        = useNavigate();
  const { tenant }      = useActiveTenant();
  const scan            = useLatestScan(tenant.id);
  const [query, setQuery]           = useState('');
  const [family, setFamily]         = useState<Family | 'all'>('all');
  const [severity, setSeverity]     = useState<Severity | 'all'>('all');
  const [status, setStatus]         = useState<StatusFilter>('all');

  const resultByControlId = useMemo(() => {
    const map = new Map<string, string>();
    scan?.results.forEach((r) => map.set(r.controlId, r.status));
    return map;
  }, [scan]);

  const filtered = useMemo(() => filterControls(
    controls,
    { query, family, severity, status, resultByControlId },
  ), [query, family, severity, status, resultByControlId]);

  const familyI18n = (f: Family) => t(`family.${f}`);
  const severityI18n = (s: Severity | 'all'): string => {
    if (s === 'all') return t('controls.filter.allF');
    return t(`severity.${s}`);
  };
  const statusI18n = (s: StatusFilter): string => {
    switch (s) {
      case 'all':     return t('controls.filter.allM');
      case 'pass':    return t('controls.filter.passPlural');
      case 'warning': return t('controls.filter.warnPlural');
      case 'fail':    return t('controls.filter.failPlural');
      case 'manual':  return t('controls.filter.manualPlural');
    }
  };

  return (
    <div>
      {/* CSS row hover effect : on doit utiliser un <style> dedicate car les
          composants Fluent UI v9 TableRow ne permettent pas de styler le hover
          via inline style. On cible la classe data-nis365-clickable-row. */}
      <style>{`
        tr[data-nis365-clickable-row] {
          cursor: pointer;
          transition: background-color 0.12s ease;
        }
        tr[data-nis365-clickable-row]:hover {
          background-color: ${tokens.colorNeutralBackground1Hover};
        }
        tr[data-nis365-clickable-row]:hover .nis365-row-chevron {
          opacity: 1;
          transform: translateX(2px);
        }
        .nis365-row-chevron {
          opacity: 0.35;
          transition: opacity 0.15s ease, transform 0.15s ease;
          color: ${tokens.colorBrandForeground1};
        }
      `}</style>

      <PageHeader
        title={t('controls.title')}
        description={t('controls.subtitleResults', { count: controls.length, tenant: tenant.name })}
      />

      <Card>
        <Table arial-label={t('controls.title')}>
          <TableHeader>
            {/* Ligne 1 : titres de colonnes */}
            <TableRow>
              <TableHeaderCell>{t('controls.list.id')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.table.family')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.list.control')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.table.severity')}</TableHeaderCell>
              <TableHeaderCell>{t('controls.filter.status')}</TableHeaderCell>
              <TableHeaderCell style={{ whiteSpace: 'nowrap' }}>
                <span role="img" aria-label="Belgique" style={{ marginRight: 4 }}>🇧🇪</span>
                CyFun
              </TableHeaderCell>
              <TableHeaderCell>{t('controls.list.references')}</TableHeaderCell>
              {/* Colonne dediee chevron */}
              <TableHeaderCell style={{ width: 32 }}></TableHeaderCell>
            </TableRow>
            {/* Ligne 2 : filtres inline sous chaque colonne (cf. demande user
                "j'aimerais les filtres au-dessus des colonnes"). */}
            <TableRow>
              <TableHeaderCell />
              <TableHeaderCell>
                <Dropdown
                  size="small"
                  placeholder={t('controls.filter.allF')}
                  value={family === 'all' ? t('controls.filter.allF') : familyLabels[family]}
                  selectedOptions={[family]}
                  onOptionSelect={(_, d) => setFamily((d.optionValue as Family | 'all') ?? 'all')}
                  style={{ minWidth: 100 }}
                >
                  <Option value="all">{t('controls.filter.allF')}</Option>
                  <Option value="identity">{familyI18n('identity')}</Option>
                  <Option value="email">{familyI18n('email')}</Option>
                  <Option value="data">{familyI18n('data')}</Option>
                  <Option value="logs">{familyI18n('logs')}</Option>
                  <Option value="endpoint">{familyI18n('endpoint')}</Option>
                  <Option value="collab">{familyI18n('collab')}</Option>
                  <Option value="apps">{familyI18n('apps')}</Option>
                </Dropdown>
              </TableHeaderCell>
              <TableHeaderCell>
                <Input
                  size="small"
                  placeholder={t('controls.filter.searchPlaceholder')}
                  value={query}
                  contentBefore={<Search20Regular />}
                  onChange={(_, d) => setQuery(d.value)}
                  style={{ width: '100%' }}
                />
              </TableHeaderCell>
              <TableHeaderCell>
                <Dropdown
                  size="small"
                  placeholder={t('controls.filter.allF')}
                  value={severityI18n(severity)}
                  selectedOptions={[severity]}
                  onOptionSelect={(_, d) => setSeverity((d.optionValue as Severity | 'all') ?? 'all')}
                  style={{ minWidth: 100 }}
                >
                  <Option value="all">{t('controls.filter.allF')}</Option>
                  <Option value="critical">{t('severity.critical')}</Option>
                  <Option value="high">{t('severity.high')}</Option>
                  <Option value="medium">{t('severity.medium')}</Option>
                  <Option value="low">{t('severity.low')}</Option>
                </Dropdown>
              </TableHeaderCell>
              <TableHeaderCell>
                <Dropdown
                  size="small"
                  placeholder={t('controls.filter.allM')}
                  value={statusI18n(status)}
                  selectedOptions={[status]}
                  onOptionSelect={(_, d) => setStatus((d.optionValue as StatusFilter) ?? 'all')}
                  style={{ minWidth: 110 }}
                >
                  <Option value="all">{t('controls.filter.allM')}</Option>
                  <Option value="pass">{t('controls.filter.passPlural')}</Option>
                  <Option value="warning">{t('controls.filter.warnPlural')}</Option>
                  <Option value="fail">{t('controls.filter.failPlural')}</Option>
                  <Option value="manual">{t('controls.filter.manualPlural')}</Option>
                </Dropdown>
              </TableHeaderCell>
              <TableHeaderCell />
              <TableHeaderCell />
              <TableHeaderCell />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((c) => {
              const statusForControl = resultByControlId.get(c.id);
              // Liste de tous les niveaux CyFun mappes (avec doublons si plusieurs
              // refs au meme niveau) - le user veut voir tous les tags individuellement
              // au lieu d un "Essential x2".
              const cyfunLevels = c.references
                .filter((r) => r.framework === 'CyFun' && r.cyfunLevel)
                .map((r) => r.cyfunLevel as NonNullable<ControlReference['cyfunLevel']>);
              const nonCyfunRefs = c.references.filter((r) => r.framework !== 'CyFun');

              return (
                <TableRow
                  key={c.id}
                  data-nis365-clickable-row
                  onClick={() => navigate(`/controls/${c.id}`)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(`/controls/${c.id}`);
                    }
                  }}
                >
                  <TableCell>
                    <span style={{ color: tokens.colorBrandForeground1, fontWeight: 600 }}>
                      {c.id}
                    </span>
                  </TableCell>
                  <TableCell><FamilyBadge family={c.family} /></TableCell>
                  <TableCell>
                    <Body1 style={{ fontWeight: 600 }}>{c.title}</Body1>
                    <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block' }}>
                      {c.description.slice(0, 110)}{c.description.length > 110 ? '...' : ''}
                    </Caption1>
                  </TableCell>
                  <TableCell><SeverityBadge severity={c.severity} /></TableCell>
                  <TableCell>
                    {statusForControl
                      ? <StatusBadge status={statusForControl as any} />
                      : <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{t('controls.list.notScanned')}</Caption1>}
                  </TableCell>
                  {/* CyFun BE - tous les tags individuellement (plus de troncature "x2") */}
                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {cyfunLevels.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {cyfunLevels.map((lvl, i) => (
                          <Badge
                            key={`${lvl}-${i}`}
                            appearance="filled"
                            style={{
                              backgroundColor: CYFUN_LEVEL_COLOR[lvl].bg,
                              color:           CYFUN_LEVEL_COLOR[lvl].fg,
                              fontWeight:      700,
                              minWidth:        70,
                              textAlign:       'center',
                            }}
                            title={t(`cyfun.levelFull.${lvl.toLowerCase()}`)}
                          >
                            {lvl}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>—</Caption1>
                    )}
                  </TableCell>
                  {/* References hors CyFun */}
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {nonCyfunRefs.slice(0, 3).map((r, i) => (
                        <FrameworkInlineText key={i} reference={r} />
                      ))}
                      {nonCyfunRefs.length > 3 && (
                        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                          {t('controls.list.moreRefs', { count: nonCyfunRefs.length - 3 })}
                        </Caption1>
                      )}
                    </div>
                  </TableCell>
                  {/* Chevron d'ouverture */}
                  <TableCell style={{ width: 32, textAlign: 'right' }}>
                    <ChevronRight20Regular className="nis365-row-chevron" />
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell>
                  <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
                    {t('controls.list.noMatch')}
                  </Body1>
                </TableCell>
                <TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell /><TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ---------- Helpers ----------------------------------------------------------

function filterControls(
  list: ControlDefinition[],
  args: {
    query:    string;
    family:   Family | 'all';
    severity: Severity | 'all';
    status:   StatusFilter;
    resultByControlId: Map<string, string>;
  },
): ControlDefinition[] {
  const q = args.query.trim().toLowerCase();
  return list.filter((c) => {
    if (args.family !== 'all'   && c.family   !== args.family)   return false;
    if (args.severity !== 'all' && c.severity !== args.severity) return false;
    if (args.status !== 'all') {
      const s = args.resultByControlId.get(c.id);
      if (s !== args.status) return false;
    }
    if (!q) return true;
    return (
      c.id.toLowerCase().includes(q) ||
      c.title.toLowerCase().includes(q) ||
      c.description.toLowerCase().includes(q) ||
      c.references.some((r) => r.ref.toLowerCase().includes(q))
    );
  });
}

// FilterField retiree - les filtres sont maintenant inline dans les
// TableHeaderCells (cf. demande user "filtres au-dessus des colonnes").
